import { Link } from 'react-router-dom';
import { ShoppingBag, Users, Mail, Sparkles, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const CTAAbout = () => {
  const cards = [
    {
      icon: <ShoppingBag className="w-8 h-8" />,
      title: 'Shop Authentic',
      description: 'Discover unique Bengali crafts and bring heritage into your home',
      to: '/shop',
      label: 'Browse Products',
      primary: true,
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Support Artisans',
      description: 'Every purchase directly supports traditional artisans and their families',
      to: '/artisans',
      label: 'Meet Artisans',
      primary: false,
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: 'Get in Touch',
      description: 'Have questions or want to collaborate? We\'d love to hear from you',
      to: '/contact',
      label: 'Contact Us',
      primary: false,
    },
  ];

  return (
    <section className="py-20 md:py-24 bg-gradient-to-br from-cocoa-900 via-clay-700 to-saffron-600 text-white relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-saffron-400/15 rounded-full blur-3xl animate-pulse-soft"></div>
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-rose-400/15 rounded-full blur-3xl animate-pulse-soft"></div>

      <div className="relative container mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/25 text-saffron-100 rounded-full text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4" />
              Join Our Journey
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Join Our Journey
            </h2>
            <p className="text-xl text-cream-100/90 max-w-3xl mx-auto">
              Whether you're looking for authentic crafts or want to support traditional artisans,
              there's a place for you in the তন্তিকা community.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {cards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="bg-white/10 backdrop-blur-md border border-white/15 rounded-3xl p-8 text-center hover:bg-white/20 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/15 border border-white/20 rounded-full mb-6">
                  {card.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                <p className="text-cream-100/85 mb-6">{card.description}</p>
                <Link
                  to={card.to}
                  className={`group inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    card.primary
                      ? 'bg-white text-clay-700 hover:bg-cream-100 hover:-translate-y-0.5'
                      : 'border-2 border-white/70 text-white hover:bg-white/10'
                  }`}
                >
                  {card.label}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-cream-100/90 mb-8 max-w-2xl mx-auto text-lg">
              তন্তিকা is more than a brand - it's a movement to preserve cultural heritage
              while embracing modern commerce.
            </p>
            <Link
              to="/register"
              className="inline-block bg-white text-clay-700 px-10 py-4 rounded-full font-bold text-lg hover:bg-cream-100 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
            >
              Join Our Community
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTAAbout;
